import { useQueryClient } from "@tanstack/react-query";
import { Upload } from "lucide-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import useLocationMediaCatalogueList from "../../../hooks/mediaCatalogue/useLocationMediaCatalogueList";
import { useMe } from "../../../hooks/queries";
import { AccountType } from "../../../models/AccountData";
import { FeatureUseLimitCalculator } from "../../../models/featureUseLimits/FeatureUseLimitsCalculator";
import { FeatureUseStatus, OrgFeatures } from "../../../models/featureUseLimits/FeatureUseLimitsData.const";
import { MediaCatalogueItemCard } from "../../MediaCatalogueComponents/MediaCatalogueItemCard";
import { ModalUploadMediaCatalogue } from "../../MediaCatalogueComponents/ModalUploadMediaCatalogue";

import "./styles.scss";

interface IMediaCatalogueSectionProps {
  locationId: string;
}

const PREVIEW_LIMIT = 5;

export const MediaCatalogueSection: React.FunctionComponent<
  IMediaCatalogueSectionProps
> = ({ locationId }) => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`facilityDetailsPage.mediaCatalogue.${key}`);
  const history = useHistory();
  const queryClient = useQueryClient();

  const { data } = useLocationMediaCatalogueList(locationId);
  const items = Array.isArray(data) ? data : [];
  const { data: me } = useMe();

  const [showUploadModal, setShowUploadModal] = useState(false);

  const canUpload =
    me?.type === AccountType.ADMIN ||
    me?.type === AccountType.LOC_ADMIN ||
    (me?.type === AccountType.ORG_ADMIN &&
      FeatureUseLimitCalculator.getFeatureUseStatus(
        OrgFeatures.USE_LOCAL_POST,
        me.organization?.commonFeatureUseLimit
      ) === FeatureUseStatus.CAN_USE);

  const handleViewMore = () => {
    history.push(`/location/${locationId}/media-catalogue`);
  };

  const handleUploadSuccess = () => {
    queryClient.invalidateQueries(["location.mediaCatalogue", locationId]);
  };

  return (
    <div className="media-catalogue-section-container">
      <div className="catalogue-preview-list">
        {items.slice(0, PREVIEW_LIMIT).map((item) => (
          <MediaCatalogueItemCard key={item.id} item={item} />
        ))}
        {items.length === 0 && (
          <p className="no-items-text">{t("no_items")}</p>
        )}
      </div>
      <div className="catalogue-button-group">
        {items.length > 0 && (
          <button className="btn btn-border" onClick={handleViewMore}>
            {t("view_more")}
          </button>
        )}
        {canUpload && (
          <button
            className="btn btn-blue btn-upload-catalogue"
            onClick={() => setShowUploadModal(true)}
          >
            <Upload size={16} color="white" />
            {t("upload")}
          </button>
        )}
      </div>
      {showUploadModal && (
        <ModalUploadMediaCatalogue
          locationId={locationId}
          onClose={() => setShowUploadModal(false)}
          onSuccess={handleUploadSuccess}
        />
      )}
    </div>
  );
};

export default MediaCatalogueSection;
